/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import { createContext, useContext } from 'react';
import type { AgentWorkspaceWidgetSource, ExpandedAgentArtifact } from './types';

export const AGENT_WORKSPACE_PATH = '/agent/workspace';

/** `/agent/workspace/<id>` → the conversation id; the bare path shows the active conversation. */
export const conversationIdFromPath = (pathname: string) => {
  const rest = pathname.slice(AGENT_WORKSPACE_PATH.length).replace(/^\/+/, '').split('/')[0];
  if (!rest) return undefined;
  try {
    return decodeURIComponent(rest);
  } catch {
    // A malformed escape in a hand-edited URL (`/agent/workspace/%`) names no conversation.
    return undefined;
  }
};

/** Each conversation's workspace is its own path, so the window keeps one tab per conversation. */
export const getAgentWorkspacePath = (conversationId: string) =>
  `${AGENT_WORKSPACE_PATH}/${encodeURIComponent(conversationId)}`;

export interface AgentWorkspaceContextValue {
  /** The Agent's active conversation. */
  conversationId: string | undefined;
  /**
   * Adds the artifact to the active conversation's workspace and opens its tab in the
   * workspace window. The title names that tab; the source (where it sits in the transcript)
   * lets the workspace be saved to the server.
   */
  expandArtifact: (
    artifact: ExpandedAgentArtifact,
    conversationTitle?: string,
    source?: AgentWorkspaceWidgetSource,
  ) => void;
  /** Opens (or brings forward) the active conversation's workspace tab. */
  openWorkspace: () => void;
  /** The widget last expanded, highlighted and scrolled into view by the window. */
  focusedWidgetId: string | undefined;
  /** Bumped on every expand, so expanding the focused widget again scrolls to it again. */
  focusRequest: number;
}

export const AgentWorkspaceContext = createContext<AgentWorkspaceContextValue | null>(null);

export const useAgentWorkspaceContext = () => useContext(AgentWorkspaceContext);
